import React, { useState, useEffect } from 'react';
import { 
  Download, 
  Wifi, 
  WifiOff, 
  Bell, 
  BellOff, 
  RefreshCw, 
  HardDrive, 
  Smartphone, 
  CheckCircle, 
  XCircle, 
  Info,
  Monitor,
  Chrome,
  Globe,
  Zap,
  Database,
  Shield,
  Signal,
  AlertCircle,
  ArrowLeft,
  Settings,
  ExternalLink
} from 'lucide-react';
import { usePWA } from '../hooks/usePWA';

/**
 * PWAScreen - Comprehensive PWA management and information screen
 * Shows installation status, offline capabilities, notifications, cache info, and more
 */
const PWAScreen = ({ onBack, showToast, appTheme = 'blue' }) => {
  const { 
    isInstallable, 
    isInstalled, 
    isOnline, 
    updateAvailable,
    installApp,
    updateApp 
  } = usePWA();

  const [notificationPermission, setNotificationPermission] = useState(Notification?.permission || 'default');
  const [cacheSize, setCacheSize] = useState(null);
  const [serviceWorkerStatus, setServiceWorkerStatus] = useState('checking');
  const [storageEstimate, setStorageEstimate] = useState(null);
  const [connectionType, setConnectionType] = useState('unknown');
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);

  useEffect(() => {
    checkServiceWorkerStatus();
    checkStorageUsage();
    checkConnectionType();
  }, []);

  const checkServiceWorkerStatus = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        setServiceWorkerStatus(registration.active ? 'active' : 'inactive');
      } catch (error) {
        setServiceWorkerStatus('error');
      }
    } else {
      setServiceWorkerStatus('unsupported');
    }
  };

  const checkStorageUsage = async () => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        setStorageEstimate(estimate);
        
        // Calculate cache size (approximate)
        if (estimate.usage) {
          const sizeInMB = (estimate.usage / (1024 * 1024)).toFixed(2);
          setCacheSize(sizeInMB);
        }
      } catch (error) {
        console.error('Failed to estimate storage:', error);
      }
    }
  };

  const checkConnectionType = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      setConnectionType(connection.effectiveType || 'unknown');
      connection.addEventListener('change', () => {
        setConnectionType(connection.effectiveType || 'unknown');
      });
    }
  };

  const handleNotificationPermission = async () => {
    if (!('Notification' in window)) {
      if (showToast) {
        showToast('This browser does not support notifications', 'warning');
      }
      return;
    }

    if (notificationPermission === 'granted') {
      // Show test notification
      new Notification('Milk App', {
        body: 'Notifications are enabled! 🔔',
        icon: '/logo-192.png',
        badge: '/logo-192.png'
      });
      return;
    }

    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    
    if (permission === 'granted') {
      new Notification('Milk App', {
        body: 'Notifications enabled successfully! 🎉',
        icon: '/logo-192.png',
        badge: '/logo-192.png'
      });
    }
  };

  const handleInstall = async () => {
    const installed = await installApp();
    if (installed && showToast) {
      showToast('App installed successfully!', 'success');
    }
  };

  const handleUpdate = () => {
    if (window.confirm('Update available! The app will refresh after updating. Continue?')) {
      updateApp();
    }
  };

  const clearCache = async () => {
    if (window.confirm('Clear all cached data? You may need to reload the app.')) {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        if (showToast) {
          showToast('Cache cleared! Reloading...', 'success');
        }
        window.location.reload();
      }
    }
  };

  const getStoragePercentage = () => {
    if (!storageEstimate || !storageEstimate.quota) return 0;
    return ((storageEstimate.usage / storageEstimate.quota) * 100).toFixed(1);
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb > 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  // Header style
  const headerStyle = appTheme === 'white'
    ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
    : { background: 'var(--primary-gradient)', color: 'var(--text-light)' };

  // Card class
  const cardClass = appTheme === 'white'
    ? 'glass-card-white-theme'
    : 'bg-white rounded-2xl shadow-lg p-6 space-y-4';

  // Card title/text
  const cardTitleClass = appTheme === 'white' ? 'text-lg font-bold text-gray-800 flex items-center gap-2 mb-5' : 'text-lg font-bold text-gray-800 flex items-center gap-2 mb-5';
  const cardTextClass = appTheme === 'white' ? 'text-gray-700 text-sm' : 'text-gray-600 text-sm';

  return (
    <div className="min-h-screen pb-20" style={{ background: 'var(--primary-bg)', color: 'var(--text-main)' }}>
      {/* Header */}
      <div className="p-6 sticky top-0 z-10 shadow-lg" style={headerStyle}>
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">PWA Settings</h1>
            <p className={appTheme === 'white' ? 'text-gray-500 text-sm' : 'text-blue-100 text-sm'}>Manage app installation & features</p>
          </div>
          <Smartphone className="w-8 h-8 opacity-80" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6 space-y-6">
        
        {/* Installation Status Card */}
        <div className={cardClass}>
          <div className="flex items-start justify-between mb-5">
            <div>
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-500" />
                Installation Status
              </h2>
            </div>
            {isInstalled ? (
              <span className="px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Installed
              </span>
            ) : (
              <span className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                Browser Mode
              </span>
            )}
          </div>

          {isInstalled ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <p className="text-green-800 text-sm mb-2">
                ✅ App is installed on your device
              </p>
              <p className="text-green-600 text-xs">
                You can access the app from your home screen anytime
              </p>
            </div>
          ) : isInstallable ? (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Install this app for a better experience with offline support and faster loading
              </p>
              <button
                onClick={handleInstall}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Install App Now
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                App installation is not available on this browser
              </p>
              <button
                onClick={() => setShowInstallInstructions(!showInstallInstructions)}
                className="text-blue-600 text-sm font-semibold flex items-center gap-1 hover:underline"
              >
                <Info className="w-4 h-4" />
                How to install on different browsers
              </button>
              
              {showInstallInstructions && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-3 text-sm">
                  <div className="font-semibold text-blue-900 flex items-center gap-2">
                    <Chrome className="w-4 h-4" />
                    Chrome/Edge (Desktop):
                  </div>
                  <p className="text-blue-800 text-xs pl-6">
                    Look for the install icon (⊕) in the address bar, or go to Menu → Install Milk App
                  </p>
                  
                  <div className="font-semibold text-blue-900 flex items-center gap-2 mt-3">
                    <Smartphone className="w-4 h-4" />
                    Safari (iOS):
                  </div>
                  <p className="text-blue-800 text-xs pl-6">
                    Tap Share button → Add to Home Screen
                  </p>
                  
                  <div className="font-semibold text-blue-900 flex items-center gap-2 mt-3">
                    <Globe className="w-4 h-4" />
                    Chrome (Android):
                  </div>
                  <p className="text-blue-800 text-xs pl-6">
                    Tap Menu (⋮) → Add to Home screen or Install app
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Update Available */}
        {updateAvailable && (
          <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-7 shadow-lg animate-pulse">
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-start gap-4">
                <RefreshCw className="w-6 h-6 text-orange-600 mt-1" />
                <div>
                  <h3 className="font-bold text-orange-900 mb-2">Update Available</h3>
                  <p className="text-sm text-orange-700">
                    A new version of the app is ready to install
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleUpdate}
              className="w-full bg-orange-500 text-white py-4 px-6 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
            >
              Update Now
            </button>
          </div>
        )}

        {/* Connection Status Card */}
        <div className={cardClass}>
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-5">
            {isOnline ? (
              <>
                <Wifi className="w-5 h-5 text-green-500" />
                Connection Status
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-red-500" />
                Connection Status
              </>
            )}
          </h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <span className="text-gray-700 text-sm font-medium">Network Status</span>
              <span className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 ${
                isOnline 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {isOnline ? (
                  <>
                    <CheckCircle className="w-3 h-3" />
                    Online
                  </>
                ) : (
                  <>
                    <XCircle className="w-3 h-3" />
                    Offline
                  </>
                )}
              </span>
            </div>

            {connectionType !== 'unknown' && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <span className="text-gray-700 text-sm font-medium flex items-center gap-2">
                  <Signal className="w-4 h-4" />
                  Connection Type
                </span>
                <span className="px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold uppercase">
                  {connectionType}
                </span>
              </div>
            )}

            {!isOnline && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
                <p className="text-yellow-800 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    You're offline. Some features may be limited, but cached content is still available.
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Notifications Card */}
        <div className={cardClass}>
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-5">
            <Bell className="w-5 h-5 text-blue-500" />
            Push Notifications
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <span className="text-gray-700 text-sm font-medium">Notification Status</span>
              <span className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 ${
                notificationPermission === 'granted'
                  ? 'bg-green-100 text-green-700'
                  : notificationPermission === 'denied'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {notificationPermission === 'granted' ? (
                  <>
                    <CheckCircle className="w-3 h-3" />
                    Enabled
                  </>
                ) : notificationPermission === 'denied' ? (
                  <>
                    <XCircle className="w-3 h-3" />
                    Blocked
                  </>
                ) : (
                  'Not Set'
                )}
              </span>
            </div>

            {notificationPermission !== 'granted' && (
              <div className="space-y-3">
                <p className="text-gray-600 text-sm">
                  Enable notifications to receive updates about your orders, offers, and deliveries
                </p>
                {notificationPermission !== 'denied' && (
                  <button
                    onClick={handleNotificationPermission}
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transition-all"
                  >
                    Enable Notifications
                  </button>
                )}
                {notificationPermission === 'denied' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                    <p className="text-red-800 text-sm flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Notifications are blocked. Please enable them in your browser settings.
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {notificationPermission === 'granted' && (
              <button
                onClick={handleNotificationPermission}
                className="w-full bg-green-100 text-green-700 py-4 px-6 rounded-xl font-semibold hover:bg-green-200 transition-colors"
              >
                Test Notification
              </button>
            )}
          </div>
        </div>

        {/* Storage & Cache Card */}
        <div className={cardClass}>
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-5">
            <HardDrive className="w-5 h-5 text-blue-500" />
            Storage & Cache
          </h2>

          <div className="space-y-4">
            {storageEstimate && (
              <>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <span className="text-gray-700 text-sm font-medium">Storage Used</span>
                  <span className="text-gray-900 font-semibold text-sm">
                    {formatBytes(storageEstimate.usage)}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <span className="text-gray-700 text-sm font-medium">Storage Quota</span>
                  <span className="text-gray-900 font-semibold text-sm">
                    {formatBytes(storageEstimate.quota)}
                  </span>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-700 text-sm font-medium">Usage</span>
                    <span className="text-gray-900 font-semibold text-sm">
                      {getStoragePercentage()}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${getStoragePercentage()}%` }}
                    />
                  </div>
                </div>
              </>
            )}

            {cacheSize && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <span className="text-gray-700 text-sm font-medium flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Cache Size (approx)
                </span>
                <span className="text-gray-900 font-semibold text-sm">
                  {cacheSize} MB
                </span>
              </div>
            )}

            <button
              onClick={clearCache}
              className="w-full bg-red-50 text-red-700 py-4 px-6 rounded-xl font-semibold hover:bg-red-100 transition-colors border border-red-200"
            >
              Clear Cache
            </button>

            <p className="text-gray-500 text-xs text-center">
              Clearing cache will remove offline data and may require reloading
            </p>
          </div>
        </div>

        {/* Service Worker Status Card */}
        <div className={cardClass}>
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-5">
            <Settings className="w-5 h-5 text-blue-500" />
            Service Worker
          </h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <span className="text-gray-700 text-sm font-medium">Status</span>
              <span className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                serviceWorkerStatus === 'active'
                  ? 'bg-green-100 text-green-700'
                  : serviceWorkerStatus === 'unsupported'
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {serviceWorkerStatus === 'active' ? '✓ Active' : 
                 serviceWorkerStatus === 'unsupported' ? 'Unsupported' : 
                 serviceWorkerStatus === 'checking' ? 'Checking...' : 'Inactive'}
              </span>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <p className="text-blue-800 text-sm flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Service workers enable offline functionality and background sync for a better app experience
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* PWA Features Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-5">
            <Zap className="w-5 h-5 text-blue-500" />
            PWA Features
          </h2>

          <div className="space-y-3">
            {[
              { label: 'Offline Support', available: 'serviceWorker' in navigator, icon: WifiOff },
              { label: 'Push Notifications', available: 'Notification' in window, icon: Bell },
              { label: 'Background Sync', available: 'serviceWorker' in navigator && 'SyncManager' in window, icon: RefreshCw },
              { label: 'Add to Home Screen', available: true, icon: Download },
              { label: 'Fast Loading', available: 'serviceWorker' in navigator, icon: Zap },
              { label: 'Secure (HTTPS)', available: window.location.protocol === 'https:' || window.location.hostname === 'localhost', icon: Shield }
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">\n                <span className="text-gray-700 text-sm font-medium flex items-center gap-2">
                  <feature.icon className="w-4 h-4" />
                  {feature.label}
                </span>
                {feature.available ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-gray-400" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* About PWA Card */}
        <div className={appTheme === 'white' ? 'glass-card-white-theme p-7' : 'bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg p-7 text-white'}>
          <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
            <Info className="w-5 h-5" />
            About Progressive Web Apps
          </h2>
          <p className="text-blue-100 text-sm leading-relaxed mb-5">
            Progressive Web Apps (PWAs) combine the best of web and mobile apps. They're fast, reliable, and work offline.
          </p>
          <a
            href="https://web.dev/progressive-web-apps/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white font-semibold text-sm hover:underline"
          >
            Learn More
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

      </div>
    </div>
  );
};

export default PWAScreen;
